import { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close, InfoOutlined, PlayArrow } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  isLowResolutionMediaSlidePreview,
  isPresentationSlidesOnlyPreview,
} from '@/helpers/preview/preview-layer-utils';
import { PreviewRenderer } from './preview-renderer';
import { LiveSlideControlProvider } from '@/components/preview/live-slide-control.context';
import type { PresentationSlideNavigationScope } from '@/components/preview/preview-presentation-slides-layer.types';
import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import type { MediaProcessingInstructionsContext } from '@/types/media-processing-instructions.types';
import { MediaProcessingInstructionsModal } from '@/components/media/media-processing-instructions.modal';

export type PreviewProcessingInstructions = {
  insight: ProcessingInsightDto;
  context: MediaProcessingInstructionsContext;
};

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  playType: string;
  playerSyncData?: Record<string, unknown>;
  loading?: boolean;
  /** Display title (e.g. media title or "Event Settings Preview") */
  title: string;
  /**
   * When set, shows a center play control (same position as the former auto-loop pause)
   * that triggers cue/device play — typically after a confirmation in the caller.
   */
  onPlay?: () => void;
  playDisabled?: boolean;
  processingInstructions?: PreviewProcessingInstructions;
  /**
   * Item PreviewModal stays on local nav (operator pick for the next take).
   * Live/simulation nav belong on Current/Next, not this inspect modal.
   * Defaults to local for media-library and Control item preview.
   */
  slideNavigationScope?: PresentationSlideNavigationScope;
  /** Required with slideNavigationScope="live" for control-slide persistence. */
  liveSlideControl?: { entityType: string; entityId: string } | null;
}

export function PreviewModal({
  open,
  onClose,
  playerSyncData,
  loading = false,
  title,
  onPlay,
  playDisabled = false,
  processingInstructions,
  slideNavigationScope,
  liveSlideControl = null,
}: PreviewModalProps) {
  const t = useTranslations();
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const isPresentation = !loading && playerSyncData ? isPresentationSlidesOnlyPreview(playerSyncData) : false;
  const showLowResHint = !loading && playerSyncData ? isLowResolutionMediaSlidePreview(playerSyncData) : false;
  const showPreviewContent = !loading && !!playerSyncData;
  const resolvedSlideScope: PresentationSlideNavigationScope | undefined = useMemo(() => {
    if (slideNavigationScope) {
      return slideNavigationScope;
    }
    return isPresentation ? 'local' : undefined;
  }, [slideNavigationScope, isPresentation]);

  const previewBody = showPreviewContent ? (
    <PreviewRenderer
      playerSyncData={playerSyncData}
      presentationPreview={isPresentation}
      sequencePlaybackControls={false}
      slideNavigationScope={resolvedSlideScope}
    />
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        bgcolor: 'grey.900',
      }}
    >
      <CircularProgress aria-label={t('media.preview' as TranslationKey)} />
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '95vh', overflow: 'hidden' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            py: 1,
            px: 2,
          }}
        >
          <Stack spacing={0.25} sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="h6" component="span" sx={{ lineHeight: 1.3 }}>
              {t('media.preview' as TranslationKey) as string} - {title}
            </Typography>
            {showLowResHint ? (
              <Typography variant="caption" color="info" component="span" sx={{ fontWeight: 600 }}>
                {t('media.previewLowResHint' as TranslationKey)}
              </Typography>
            ) : null}
          </Stack>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'grey.500', mt: -0.5, mr: -0.5, flexShrink: 0 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              maxHeight: 'min(calc(95vh - 56px), calc((100vw - 48px) * 9 / 16))',
              mx: 'auto',
              lineHeight: 0,
            }}
          >
            {resolvedSlideScope === 'live' && liveSlideControl ? (
              <LiveSlideControlProvider value={liveSlideControl}>{previewBody}</LiveSlideControlProvider>
            ) : (
              previewBody
            )}
            {showPreviewContent && processingInstructions ? (
              <IconButton
                aria-label={t('media.processing.instructionsModal.title')}
                onClick={() => setInstructionsOpen(true)}
                size="medium"
                sx={{
                  'position': 'absolute',
                  'top': 12,
                  'right': 12,
                  'zIndex': 2001,
                  'color': 'warning.contrastText',
                  'bgcolor': 'warning.main',
                  '&:hover': { bgcolor: 'warning.dark' },
                }}
              >
                <InfoOutlined />
              </IconButton>
            ) : null}
            {showPreviewContent && onPlay ? (
              <IconButton
                aria-label={t('media.play' as TranslationKey)}
                onClick={onPlay}
                disabled={playDisabled}
                size="large"
                sx={{
                  'position': 'absolute',
                  'top': '50%',
                  'left': '50%',
                  'transform': 'translate(-50%, -50%)',
                  // Above preview layers (zIndex = layerId) and the audio toggle (1280).
                  'zIndex': 2000,
                  'color': '#fff',
                  'bgcolor': 'rgba(0,0,0,0.45)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(0,0,0,0.3)' },
                }}
              >
                <PlayArrow fontSize="large" />
              </IconButton>
            ) : null}
          </Box>
        </DialogContent>
      </Dialog>

      {processingInstructions ? (
        <MediaProcessingInstructionsModal
          open={instructionsOpen}
          onClose={() => setInstructionsOpen(false)}
          insight={processingInstructions.insight}
          context={processingInstructions.context}
        />
      ) : null}
    </>
  );
}
